import React from 'react';
import PropTypes from 'prop-types';
import { BodyText, Button, HeadingText } from '@paypalcorp/pp-react';
import PixelTag from './PixelTag';
import {
  PARTNERS_TITLE,
  MARKETPLACES_TITLE,
  CHECKOUT,
  AMPLITUDE
} from '../../../lib/constants';
import {
  recordIntent,
  productToRPSMapping
} from '../../../lib/appcenterIntent';
import {
  fireDetailsPageComponentFailureFpti,
  parseData,
  getUtmParams
} from '../../../lib/helpers';
import { Link } from 'react-router-dom';

const { PRIMARY_CTA_CLICK } = AMPLITUDE;
const getButtonItems = button => (button ? parseData(button)[0] : {});
const getAppIconItems = appIcon => (appIcon ? parseData(appIcon)[0] : {});
const getAppItemsUrlColored = appIconItems =>
  appIconItems.urlColored ? appIconItems.urlColored : '';
const getLocationValue = location =>
  location.pathname ? location.pathname.split('/') : [];
const getLocationCategory = locationValue =>
  locationValue.length !== 0 ? locationValue[locationValue.length - 2] : '';
const isPartnerMarket = locationCategory =>
  locationCategory === PARTNERS_TITLE ||
  locationCategory === MARKETPLACES_TITLE;

const getWindowLocation = (url, domain) => {
  const {
    utm_source: source,
    utm_medium: medium,
    utm_campaign: campaign,
    utm_content: content
  } = getUtmParams();

  if ((source || medium || campaign || content) && url) {
    const urlQueryParams = new URL(url, `https://www.${domain}`).search; //This line  checks if the URL has some query params.
    const divider = urlQueryParams ? '&' : '?';
    const sourceUtmParam = source ? `utm_source=${source}` : '';
    const mediumUtmParam = medium ? `&utm_medium=${medium}` : '';
    const campaignUtmParam = campaign ? `&utm_campaign=${campaign}` : '';
    const contentUtmParam = content ? `&utm_content=${content}` : '';
    return `${url}${divider}${sourceUtmParam}${mediumUtmParam}${campaignUtmParam}${contentUtmParam}`;
  }

  return url;
};

const DetailsHeader = ({
  componentContent = {},
  fptiClick,
  handleClickFPTI,
  fptiImpression,
  app = {},
  location = {},
  experimentation = {}
}) => {
  const {
    title,
    subtitle,
    button,
    appIcon,
    toolKey,
    pixelTagLink
  } = componentContent;
  const { coloredIconsExp: isColoredIconsEligible } = experimentation;
  const { context: { location: { domain } = {} } = {} } = app;
  const buttonItems = getButtonItems(button);

  const {
    text: buttonText,
    url: buttonUrl,
    subText: buttonSubtext
  } = buttonItems;
  const appIconItems = getAppIconItems(appIcon);

  const { url: imageURL } = appIconItems;
  const coloredImgUrl =
    isColoredIconsEligible && getAppItemsUrlColored(appIconItems);

  if (!title || !subtitle || !buttonText || !buttonUrl || !imageURL) {
    fireDetailsPageComponentFailureFpti({
      data: { title, subtitle, buttonText, buttonUrl, imageURL },
      componentName: 'DetailsHeader',
      fptiImpression
    });
    return null;
  }
  const locationValue = getLocationValue(location);
  const locationCategory = getLocationCategory(locationValue);
  const partnerMarket = isPartnerMarket(locationCategory);
  const ctaRedirect = url => {
    window.location = getWindowLocation(url, domain);
  };
  const isCheckout =
    toolKey === 'pp_express_checkout' && buttonUrl.includes('/setup/checkout');
  const handleClick = () => {
    handleClickFPTI('get_started');
    if (productToRPSMapping[toolKey]) {
      rpsRecordIntent(buttonUrl);
    } else if (toolKey === CHECKOUT && buttonUrl === '#') {
      handleCheckoutCase();
    } else {
      ctaRedirect(buttonUrl);
    }
  };
  const rpsRecordIntent = url => {
    recordIntent({
      csrf: app.csrf,
      productArr: [productToRPSMapping[toolKey]]
    }).finally(() => {
      ctaRedirect(url);
    });
  };

  const handleCheckoutCase = () => {
    fptiClick({
      amplitude: true,
      event_name: PRIMARY_CTA_CLICK,
      fptiLink: 'get_started'
    });
    const htmlElement = document.querySelector('#details-section-header') || {};
    window.scrollTo(0, htmlElement.offsetTop);
  };

  return (
    <section id="details-header">
      <PixelTag pixelTagLink={pixelTagLink} />
      <div className="details-header-container">
        <div className="container-one">
          <div
            className={
              isColoredIconsEligible
                ? 'details-header-image'
                : 'details-header-image old'
            }
            style={
              isColoredIconsEligible
                ? {
                    background: `url(${coloredImgUrl ||
                      imageURL}) center center no-repeat`,
                    backgroundSize: '100%'
                  }
                : {
                    background: `url(${imageURL}) center center no-repeat`,
                    backgroundSize: partnerMarket ? '100%' : '80%',
                    ...(partnerMarket && { border: '0' })
                  }
            }
          ></div>
        </div>
        <div className="container-two">
          <HeadingText
            level={1}
            dangerouslySetInnerHTML={{ __html: title }}
          ></HeadingText>
          <BodyText dangerouslySetInnerHTML={{ __html: subtitle }}></BodyText>
        </div>
        <div className="container-three">
          {!isCheckout && (
            <>
              <Button onClick={handleClick}>{buttonText}</Button>
              {buttonSubtext && <BodyText> {buttonSubtext} </BodyText>}
            </>
          )}
          {isCheckout && (
            <Link
              to={{
                pathname: '/setup/checkout/home',
                state: { prevPath: '/appcenter/acceptpayments/checkout' }
              }}
              onClick={() => {
                handleClickFPTI('get_started');
              }}
            >
              <Button>{buttonText}</Button>
              {buttonSubtext && <BodyText> {buttonSubtext} </BodyText>}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

DetailsHeader.propTypes = {
  componentContent: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  imageURL: PropTypes.string,
  buttonUrl: PropTypes.string,
  buttonText: PropTypes.string,
  buttonSubtext: PropTypes.string,
  app: PropTypes.object,
  handleClickFPTI: PropTypes.func,
  fptiClick: PropTypes.func,
  location: PropTypes.object,
  fptiImpression: PropTypes.func.isRequired,
  toolName: PropTypes.string,
  experimentation: PropTypes.object
};

export default DetailsHeader;
